
















































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import {
  ContactTypeFieldIndexes,
  GroupLevelAttributeContactsType,
  GroupLevelAttributeValueStatus
} from '@/store/modules/admin/types/group-level-attribute.types';
import { isEmail } from '../../../../jbi-shared/util/validate-email-domains.util';
import { isContact as isPhone } from '@/jbi-shared/util/validate-email-domains.util';
import { getErrorMessage } from '@/jbi-shared/util/error-formatter';
import { GroupLevelAttributeValue } from '@/jbi-shared/types/jaas-group-level-attributes.types';
import { deepCopy } from '@/jbi-shared/util/deep-copy.util';
import DraggableIcon from './DraggableIcon.vue';
import AttributeActionButtons from './AttributeActionButtons.vue';

@Component({
  components: {
    DraggableIcon,
    AttributeActionButtons
  }
})
export default class GroupLevelAttributeContactField extends Vue {
  @Prop() attributeValue!: GroupLevelAttributeValue;
  @Prop() fieldClasses!: any;
  @Prop() duplicationError!: boolean;
  @Prop({ default: false }) isSettings!: boolean;
  @Prop({ default: false }) overrideDisableDraggable!: boolean;
  @Prop({ default: false }) overrideDisableAttributeButton!: boolean;
  @Prop() deleteAttribute!: () => void;
  @Prop() updateAttributeValue!: (attribute: GroupLevelAttributeValue) => void;
  @Prop() toggleAttributeRequire!: (
    attribute: GroupLevelAttributeValue
  ) => void;
  @Prop() editAttribute!: (attribute: GroupLevelAttributeValue) => void;
  showField: boolean = false;

  isEmailFormatCorrect: boolean = true;
  isPhoneFormatCorrect: boolean = true;

  listOfContacts: GroupLevelAttributeContactsType[] = [];
  clonedListOfContacts: GroupLevelAttributeContactsType[] = [];

  /**
   * Whenever a new contact is created, initialize object with
   * empty properties to be populated later.
   */
  newContact: GroupLevelAttributeContactsType = {
    id: undefined,
    contactOptions: [],
    hasFieldError: true,
    errorMessage: {
      nameErrorMessage: '',
      emailErrorMessage: '',
      phoneErrorMessage: ''
    }
  };

  get contactTypeFieldIndexes() {
    return ContactTypeFieldIndexes;
  }

  // Computed property so changes from the value from parent component are synced
  get isRequired() {
    return this.attributeValue.groupLevelAttributeSpec.isRequired;
  }

  get attributeHasFieldError() {
    return this.clonedListOfContacts.some((contacts) => contacts.hasFieldError);
  }

  mounted() {
    let hasValue: boolean = false;

    if (this.attributeValue.value) {
      hasValue = (this.attributeValue
        .value as GroupLevelAttributeContactsType[]).some((contact) =>
        contact.contactOptions.some((options) => options.value)
      );
    }

    if (hasValue) {
      this.assignExistingValues(false);
    } else {
      this.assignExistingValues(true);
    }

    /**
     * A cloned list is used for rendering the contacts list. This was
     * created here to prevent unexpected mutation of prop passed in by
     * the parent component. In this case, `listOfContacts` is derived
     * from the `attributeValue` prop passed in from CourseLevelAttributes.
     */
    this.clonedListOfContacts = deepCopy(this.listOfContacts);
  }

  /**
   * Initialise `errorMessage` property with empty string to prevent
   * object potentially resolving as undefined.
   */
  assignExistingValues(fieldError: boolean) {
    this.listOfContacts = (this.attributeValue
      .value as GroupLevelAttributeContactsType[]).map((contact) => ({
      ...contact,
      hasFieldError: fieldError,
      errorMessage: {
        nameErrorMessage: '',
        emailErrorMessage: '',
        phoneErrorMessage: ''
      }
    }));
  }

  // Generate empty default contact options for a new contact object
  createDefaultContactOptions() {
    return [
      {
        value: '',
        status: GroupLevelAttributeValueStatus.ACTIVE,
        isRequired: true,
        placeholder: 'Name',
        attributeType: {
          id: 1,
          type: 'text'
        }
      },
      {
        value: '',
        status: GroupLevelAttributeValueStatus.ACTIVE,
        isRequired: true,
        placeholder: 'Email',
        attributeType: {
          id: 4,
          type: 'email'
        }
      },
      {
        value: '',
        status: GroupLevelAttributeValueStatus.ACTIVE,
        isRequired: true,
        placeholder: 'Phone',
        attributeType: {
          id: 12,
          type: 'phone'
        }
      }
    ];
  }

  // Dynamically render error message for nested fields
  getErrorMessage(index: number, field: string) {
    switch (field) {
      case 'name':
        return this.clonedListOfContacts[index].errorMessage?.nameErrorMessage;
      case 'email':
        return this.clonedListOfContacts[index].errorMessage?.emailErrorMessage;
      case 'phone':
        return this.clonedListOfContacts[index].errorMessage?.phoneErrorMessage;
      default:
        break;
    }
  }

  updateParent() {
    this.attributeValue.value = this.clonedListOfContacts;
    this.updateAttributeValue(this.attributeValue);
  }

  validateName(index: number): void {
    const currentContact: GroupLevelAttributeContactsType = this
      .clonedListOfContacts[index];
    const hasValue = !!currentContact.contactOptions[0].value;
    const name = hasValue
      ? (currentContact.contactOptions[ContactTypeFieldIndexes.NAME]
          .value as string)
      : '';

    let errorMessage: string;
    if (this.isRequired && !hasValue) {
      errorMessage = `${
        currentContact.contactOptions[ContactTypeFieldIndexes.NAME].placeholder
      } is required.`;
      currentContact.errorMessage!.nameErrorMessage = errorMessage;
      currentContact.hasFieldError = true;
      this.updateParent();
      return;
    }

    // Additional check if the name contains only allowed characters
    if (!/^[a-zA-Z\s]+$/.test(name)) {
      errorMessage = 'Name can only contain letters and spaces.';
      currentContact.errorMessage!.nameErrorMessage = errorMessage;
      currentContact.hasFieldError = true;
      this.updateParent();
      return;
    }

    errorMessage = ''; // Clear the error message if valid
    currentContact.hasFieldError = false;
    currentContact.errorMessage!.nameErrorMessage = errorMessage;

    this.updateParent();
  }

  validateEmailFormat(index: number): void {
    const currentContact: GroupLevelAttributeContactsType = this
      .clonedListOfContacts[index];
    const hasValue = !!currentContact.contactOptions[
      ContactTypeFieldIndexes.EMAIL
    ].value;
    let errorMessage: string;
    if (this.isRequired && !hasValue) {
      errorMessage = `${
        currentContact.contactOptions[ContactTypeFieldIndexes.EMAIL].placeholder
      } is required.`;
      currentContact.errorMessage!.emailErrorMessage = errorMessage;
      currentContact.hasFieldError = true;
      this.updateParent();
      return;
    }

    // Run validator when there's value
    this.isEmailFormatCorrect = isEmail(
      currentContact.contactOptions[ContactTypeFieldIndexes.EMAIL]
        .value as string
    );

    if (!this.isEmailFormatCorrect) {
      errorMessage = 'Invalid email format.';
      currentContact.hasFieldError = true;
    } else {
      errorMessage = '';
      currentContact.hasFieldError = false;
    }

    currentContact.errorMessage!.emailErrorMessage = errorMessage;
    this.updateParent();
  }

  validatePhoneFormat(currentContactIndex: number): void {
    const currentContact: GroupLevelAttributeContactsType = this
      .clonedListOfContacts[currentContactIndex];
    const hasValue = !!currentContact.contactOptions[
      ContactTypeFieldIndexes.PHONE
    ].value;

    let errorMessage: string;
    if (this.isRequired && !hasValue) {
      errorMessage = `${
        currentContact.contactOptions[ContactTypeFieldIndexes.PHONE].placeholder
      } is required.`;
      currentContact.errorMessage!.phoneErrorMessage = errorMessage;
      currentContact.hasFieldError = true;
      this.updateAttributeValue(this.attributeValue);
      return;
    }
    // Run validator when there's value
    this.isPhoneFormatCorrect = isPhone(
      currentContact.contactOptions[ContactTypeFieldIndexes.PHONE]
        .value as string
    );

    if (!this.isPhoneFormatCorrect) {
      errorMessage = 'Invalid phone format.';
      currentContact.hasFieldError = true;
    } else {
      errorMessage = '';
      currentContact.hasFieldError = false;
    }

    currentContact.errorMessage!.phoneErrorMessage = errorMessage;
    this.updateParent();
  }

  removeContactFromList(valueIndex: number) {
    this.clonedListOfContacts.splice(valueIndex, 1);
    this.attributeValue.value = this.clonedListOfContacts;
  }

  removeContact(event: any, identifierClass: string, itemIndex: number) {
    if (event.target.parentElement.className.includes(identifierClass)) {
      this.removeContactFromList(itemIndex);
    }
  }

  /**
   * Append cloned list with default contact object with
   * initialised id and contact options.
   */
  addContact() {
    this.clonedListOfContacts.push({
      ...deepCopy(this.newContact),
      id: this.clonedListOfContacts.length + 1,
      contactOptions: this.createDefaultContactOptions()
    });

    this.attributeValue.value = this.clonedListOfContacts;
  }

  @Watch('attributeHasFieldError')
  onContactFieldErrorsUpdated() {
    // Update parent field error state
    this.attributeValue.hasFieldError = this.attributeHasFieldError;
  }
}
